// Shared scaffold for every Explore › Statistics page.
// A page supplies a title, blurb, the chart(s) as children, and the
// { columns, rows } supporting data; this renders the consistent layout,
// the supporting table, the CSV download, and loading/error states.

import React from 'react';
import {
  Box, Typography, Paper, CircularProgress, Alert, Button, Breadcrumbs, Link,
  Table, TableHead, TableBody, TableRow, TableCell, TableContainer, Divider,
  TableSortLabel,
} from '@mui/material';
import DownloadIcon from '@mui/icons-material/Download';
import { Link as RouterLink } from 'react-router-dom';
import { downloadCSV } from './csv';
import useTableSort from '../../hooks/useTableSort';

const MAX_TABLE_ROWS = 500;

// Render a single table cell, hyperlinking it to the entity detail page
// when the backend resolved this column's value to an entity ID (#133).
function StatCell({ value, link }) {
  // External link (e.g. an archived report URL) — open in a new tab (#221).
  if (link && link.type === 'external' && link.url && value) {
    return (
      <Link href={link.url} target="_blank" rel="noopener noreferrer" underline="hover">
        {value}
      </Link>
    );
  }
  if (link && link.id !== undefined && link.id !== null && value) {
    const routeBase = {
      parliamentarian: '/parliamentarians',
      party: '/parties',
      electorate: '/electorates',
      committee: '/committees',
      bill: '/bills',
    }[link.type];
    if (routeBase) {
      return (
        <Link
          component={RouterLink}
          to={`${routeBase}/${link.id}`}
          underline="hover"
        >
          {value}
        </Link>
      );
    }
  }
  return value;
}

export default function ExploreStatsPage({
  title,
  description,
  loading,
  error,
  columns = [],
  rows = [],
  links = [],
  csvName,
  children,
}) {
  // Pair each row with its entity-link map so user sorting keeps links
  // aligned with their row (#134). Default order is unchanged until a
  // header is clicked.
  const paired = React.useMemo(
    () => rows.map((row, i) => ({ row, link: links[i] })),
    [rows, links],
  );
  const { order, orderBy, sortedRows, handleSort } = useTableSort(
    paired,
    (pair, col) => pair.row[col],
  );

  return (
    <Box>
      <Breadcrumbs sx={{ mb: 1 }}>
        <Link component={RouterLink} to="/explore/stats" underline="hover" color="inherit">
          Explore Statistics
        </Link>
        <Typography color="text.primary">{title}</Typography>
      </Breadcrumbs>

      <Typography variant="h4" sx={{ mb: 1 }}>{title}</Typography>
      {description && (
        <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
          {description}
        </Typography>
      )}

      {loading && (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
          <CircularProgress />
        </Box>
      )}

      {error && !loading && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {String(error)}
        </Alert>
      )}

      {!loading && !error && (
        <>
          {/* Chart area supplied by the specific page */}
          {children}

          {columns.length > 0 && (
            <Paper elevation={1} sx={{ p: 2, mt: 4, borderRadius: 3 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
                <Typography variant="h6">Supporting data</Typography>
                <Button
                  size="small"
                  variant="outlined"
                  startIcon={<DownloadIcon />}
                  disabled={!rows.length}
                  onClick={() => downloadCSV(csvName || title, columns, rows)}
                >
                  Download CSV
                </Button>
              </Box>
              <Divider sx={{ mb: 1 }} />
              <TableContainer sx={{ maxHeight: 520 }}>
                <Table size="small" stickyHeader>
                  <TableHead>
                    <TableRow>
                      {columns.map((c) => (
                        <TableCell
                          key={c}
                          sx={{ fontWeight: 600 }}
                          sortDirection={orderBy === c ? order : false}
                        >
                          <TableSortLabel
                            active={orderBy === c}
                            direction={orderBy === c ? order : 'asc'}
                            onClick={() => handleSort(c)}
                          >
                            {c}
                          </TableSortLabel>
                        </TableCell>
                      ))}
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {sortedRows.slice(0, MAX_TABLE_ROWS).map((pair, i) => (
                      <TableRow key={i} hover>
                        {columns.map((c) => (
                          <TableCell key={c}>
                            <StatCell
                              value={pair.row[c]}
                              link={pair.link && pair.link[c]}
                            />
                          </TableCell>
                        ))}
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
              {rows.length > MAX_TABLE_ROWS && (
                <Typography variant="caption" color="text.secondary">
                  Showing first {MAX_TABLE_ROWS} of {rows.length} rows — download the CSV for the full data.
                </Typography>
              )}
            </Paper>
          )}
        </>
      )}
    </Box>
  );
}
